import { useState } from "react";
import { CalendarHeart, ChevronDown, HelpCircle } from "lucide-react";
import HolidayCalendarMvp from "../Attendance/HolidayCalendarMvp";
import "../../../styles/attendance-mvp.css";

export default function HolidayCalendarPage() {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100/30 p-4 md:p-6">
      {/* HERO */}
      <div
        className="relative rounded-3xl p-6 md:p-7 mb-6 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0f2d52 0%, #1a437d 55%, #2a5fa3 100%)",
          boxShadow: "0 8px 28px rgba(15, 45, 82, 0.32)",
        }}
      >
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/15 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex items-start gap-4">
          <div
            className="shrink-0 w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg"
            style={{ color: "#FFFFFF" }}
          >
            <CalendarHeart size={26} />
          </div>
          <div className="min-w-0 flex-1">
            <h1
              className="text-2xl md:text-3xl font-extrabold drop-shadow-sm"
              style={{ color: "#FFFFFF" }}
            >
              Holiday Calendar
            </h1>
            <p
              className="text-sm mt-1.5 max-w-2xl"
              style={{ color: "rgba(255, 255, 255, 0.92)" }}
            >
              Maintain organization holidays used for attendance and payroll calculations. This is
              separate from the monthly shift roster planner.
            </p>
          </div>
        </div>
      </div>

      {/* HELP GUIDE */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/60 overflow-hidden mb-6">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-blue-100/40 transition"
        >
          <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm shrink-0">
            <HelpCircle size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-blue-900">
              How to use Holiday Calendar
            </p>
            <p className="text-[11px] text-blue-700/70">
              Click here for a step-by-step guide on adding and managing holidays for your organisation
            </p>
          </div>
          <ChevronDown size={16} className={`text-blue-500 transition-transform ${showHelp ? "rotate-180" : ""}`} />
        </button>
        {showHelp && (
          <div className="px-5 pb-5 border-t border-blue-200/60">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Holiday Dashboard</p>
                  <p className="text-[11px] text-slate-500">At the top you'll see three KPI tiles — <b className="text-emerald-700">Total Holidays</b> for the year, <b className="text-amber-700">Upcoming Holidays</b> with the next date, and <b className="text-violet-700">Completed Holidays</b> that have already passed. This gives you a quick snapshot of your holiday calendar.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Apply Indian Holidays</p>
                  <p className="text-[11px] text-slate-500">Click the <b className="text-blue-700">Apply Indian Holidays</b> button to auto-add 8 fixed-date national holidays (Republic Day, Independence Day, Gandhi Jayanti, etc.). Lunar festivals like Holi, Diwali, and Eid must be added manually since their dates change every year.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Yearly Calendar &amp; Month Filter</p>
                  <p className="text-[11px] text-slate-500">The 12-month grid shows holiday counts as badges on each month. Click any month to filter the holiday list below to that month only. Change the year with the number input and click <b className="text-indigo-700">Reload</b> to view a different year.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Bulk Upload via Excel</p>
                  <p className="text-[11px] text-slate-500">Click <b className="text-blue-700">Download Template</b> to get a pre-formatted Excel file. Fill in your holiday dates and names, choose the file, click <b className="text-amber-700">Validate</b> to check for errors, then <b className="text-blue-700">Upload Template</b> to import all holidays at once.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">5</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Manual Holiday Entry</p>
                  <p className="text-[11px] text-slate-500">To add a single holiday, fill in the <b>date</b>, <b>name</b>, and select the type — <b className="text-blue-700">National</b> (mandatory day off) or <b className="text-amber-700">Optional</b> (restricted holiday). Click <b className="text-emerald-700">Add Holiday</b> to save. To edit an existing holiday, click <b>Edit</b> in the holiday list — the form loads the values for you.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">6</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Holiday List &amp; Actions</p>
                  <p className="text-[11px] text-slate-500">The full holiday table at the bottom lists every holiday with its date, name, and type. Use <b className="text-indigo-700">Edit</b> to modify a holiday or <b className="text-rose-700">Delete</b> to remove it. Toggle between viewing all holidays or filtering by the selected month.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <HolidayCalendarMvp />
    </div>
  );
}
